const { Track } = require('livekit-client');
const { LKBase } = require('./lk-base');
const { LK_EVENTS } = require('./lk-events');
const { UI } = require('./lk-ui');

class LKScreen extends LKBase {
  /**
   * Screen sharing module: presenter + viewer layout.
   * @param {HTMLElement} container
   * @param {object} options - LKBase options + screen-specific:
   * @param {boolean} [options.canPresent=false]
   * @param {boolean} [options.startWithAudio=true]
   * @param {boolean} [options.showThumbnails=true]
   * @param {boolean} [options.showControls=true]
   */
  constructor(container, options) {
    super(container, options);
    this._canPresent = options.canPresent || false;
    this._startWithAudio = options.startWithAudio !== false;
    this._showThumbnails = options.showThumbnails !== false;
    this._showControls = options.showControls !== false;
    this._isPresenting = false;
    this._currentPresenter = null;
    this._mainEl = null;
    this._thumbsEl = null;
    this._controlsEl = null;
    this._thumbTiles = new Map(); // identity → HTMLElement
  }

  // ─── Public API ──────────────────────────────────────────────────

  async connect() {
    await super.connect();
    if (this._startWithAudio) {
      try {
        await this.room.localParticipant.setMicrophoneEnabled(true);
      } catch (err) {
        console.warn('[LKScreen] Microphone unavailable:', err.message);
      }
    }
  }

  async startScreenShare(opts = {}) {
    if (!this.room || this._isPresenting) return;
    try {
      await this.room.localParticipant.setScreenShareEnabled(true, {
        audio: opts.audio !== false,
        resolution: opts.resolution || undefined
      });
      this._isPresenting = true;
      this._currentPresenter = this.room.localParticipant;
      this._updatePresenterView();
      if (this._controlsEl) {
        UI.toggleControl(this._controlsEl, 'screen', true);
      }
      this._emit(LK_EVENTS.SCREENSHARE_STARTED, { participant: this.room.localParticipant });
    } catch (err) {
      // User cancelled screen share picker
      this._emit(LK_EVENTS.ERROR, { error: err, context: 'startScreenShare' });
    }
  }

  async stopScreenShare() {
    if (!this.room || !this._isPresenting) return;
    await this.room.localParticipant.setScreenShareEnabled(false);
    this._isPresenting = false;
    if (this._currentPresenter === this.room.localParticipant) {
      this._currentPresenter = null;
    }
    this._updatePresenterView();
    if (this._controlsEl) {
      UI.toggleControl(this._controlsEl, 'screen', false);
    }
    this._emit(LK_EVENTS.SCREENSHARE_STOPPED, { participant: this.room.localParticipant });
  }

  get isPresenting() { return this._isPresenting; }
  get currentPresenter() { return this._currentPresenter; }

  // ─── UI rendering ────────────────────────────────────────────────

  _renderUI() {
    if (!this.container) return;
    this.container.classList.add('lk-container', 'lk-screen-mode');

    // Main area (screen share display)
    this._mainEl = document.createElement('div');
    this._mainEl.className = 'lk-screen-main';
    this.container.appendChild(this._mainEl);

    UI.showOverlay(this._mainEl, 'Waiting for screen share...');

    // Thumbnails sidebar
    if (this._showThumbnails) {
      this._thumbsEl = document.createElement('div');
      this._thumbsEl.className = 'lk-screen-thumbs';
      this.container.appendChild(this._thumbsEl);

      // Add local participant thumb
      this._addThumb(this.room.localParticipant, true);
      // Add remote participants
      for (const [, p] of this.room.remoteParticipants) {
        this._addThumb(p, false);
      }
    }

    // Controls
    if (this._showControls) {
      const actions = [
        {
          id: 'mic',
          icon: UI.icons.mic,
          label: 'Toggle microphone',
          onClick: () => this._toggleMic()
        }
      ];

      if (this._canPresent) {
        actions.push({
          id: 'screen',
          icon: UI.icons.screenShare,
          label: 'Share screen',
          onClick: () => this._isPresenting ? this.stopScreenShare() : this.startScreenShare()
        });
      }

      actions.push({
        id: 'hangup',
        icon: UI.icons.hangup,
        label: 'Disconnect',
        className: 'lk-ctrl-btn--danger',
        onClick: () => this.disconnect()
      });

      this._controlsEl = UI.createControls(actions);
      this.container.appendChild(this._controlsEl);
    }

    // Check if someone is already sharing
    this._detectExistingScreenShare();
  }

  _cleanupUI() {
    this._thumbTiles.clear();
    this._mainEl = null;
    this._thumbsEl = null;
    this._controlsEl = null;

    if (this.container) {
      this.container.classList.remove('lk-container', 'lk-screen-mode');
      this.container.innerHTML = '';
    }
  }

  async _toggleMic() {
    if (!this.room) return;
    const enabled = this.room.localParticipant.isMicrophoneEnabled;
    await this.room.localParticipant.setMicrophoneEnabled(!enabled);
    if (this._controlsEl) {
      UI.toggleControl(this._controlsEl, 'mic', enabled);
    }
  }

  _addThumb(participant, isLocal) {
    if (!this._thumbsEl) return;
    const tile = UI.createTile(participant, { isLocal });
    tile.classList.add('lk-screen-thumb');
    this._thumbsEl.appendChild(tile);
    this._thumbTiles.set(participant.identity, tile);
  }

  _removeThumb(participant) {
    const tile = this._thumbTiles.get(participant.identity);
    if (tile) {
      tile.remove();
      this._thumbTiles.delete(participant.identity);
    }
  }

  _detectExistingScreenShare() {
    for (const [, p] of this.room.remoteParticipants) {
      const screenPub = p.getTrackPublication(Track.Source.ScreenShare);
      if (screenPub && screenPub.track) {
        this._showScreenTrack(screenPub.track, p);
        break;
      }
    }
  }

  _showScreenTrack(track, participant) {
    if (!this._mainEl) return;
    UI.hideOverlay(this._mainEl);

    // Remove previous screen video
    const prev = this._mainEl.querySelector('.lk-screen-video');
    if (prev) prev.remove();

    const el = track.attach();
    el.className = 'lk-screen-video';
    this._mainEl.appendChild(el);
    this._currentPresenter = participant;
  }

  _hideScreenTrack(track) {
    const elements = track.detach();
    for (const el of elements) el.remove();
    this._currentPresenter = null;

    if (this._mainEl) {
      UI.showOverlay(this._mainEl, 'Waiting for screen share...');
    }
  }

  _updatePresenterView() {
    if (!this._mainEl) return;
    if (!this._currentPresenter) {
      // Clear main view
      const vid = this._mainEl.querySelector('.lk-screen-video');
      if (vid) vid.remove();
      UI.showOverlay(this._mainEl, 'Waiting for screen share...');
    }
  }

  // ─── Room event hooks ────────────────────────────────────────────

  _onParticipantConnected(participant) {
    this._addThumb(participant, false);
  }

  _onParticipantDisconnected(participant) {
    this._removeThumb(participant);
    if (this._currentPresenter && this._currentPresenter.identity === participant.identity) {
      this._currentPresenter = null;
      this._updatePresenterView();
    }
  }

  _onTrackSubscribed(track, publication, participant) {
    if (track.source === Track.Source.ScreenShare && track.kind === Track.Kind.Video) {
      this._showScreenTrack(track, participant);
      this._emit(LK_EVENTS.SCREENSHARE_STARTED, { participant });
    } else if (track.kind === Track.Kind.Video) {
      // Camera track → attach to thumbnail
      const tile = this._thumbTiles.get(participant.identity);
      if (tile) UI.attachTrackToTile(tile, track);
    } else if (track.kind === Track.Kind.Audio) {
      UI.attachAudioTrack(track);
    }
  }

  _onTrackUnsubscribed(track, publication, participant) {
    if (track.source === Track.Source.ScreenShare && track.kind === Track.Kind.Video) {
      this._hideScreenTrack(track);
      this._emit(LK_EVENTS.SCREENSHARE_STOPPED, { participant });
    } else if (track.kind === Track.Kind.Video) {
      const tile = this._thumbTiles.get(participant.identity);
      if (tile) UI.detachTrackFromTile(tile, track);
    } else if (track.kind === Track.Kind.Audio) {
      UI.detachAudioTrack(track);
    }
  }

  _onActiveSpeakersChanged(speakers) {
    for (const [, tile] of this._thumbTiles) {
      UI.setTileSpeaking(tile, false);
    }
    for (const s of speakers) {
      const tile = this._thumbTiles.get(s.identity);
      UI.setTileSpeaking(tile, true);
    }
  }
}

module.exports = { LKScreen };
